// DCMA Scoring Engine
// Takes parsed P6 project data + scoring criteria config → produces detailed score results

import type { ScoringCriterion, GradeThreshold } from './dcma-config';
import { defaultCriteria, defaultGradeThresholds, categoryLabels } from './dcma-config';
import type { P6Project, P6Activity } from './p6-parser';

export interface CriterionResult {
  criterionId: string;
  criterionName: string;
  category: string;
  categoryLabel: string;
  enabled: boolean;
  computedValue: number;
  displayValue: string;
  tier: 'good' | 'acceptable' | 'bad' | 'badLow' | 'badHigh' | 'n/a';
  tierLabel: string;
  scoreImpact: number;
  thresholdDescription: string;
  flaggedItems: FlaggedItem[];
  // Audit fields - expose the exact numerator/denominator so users can verify
  numerator: number;
  denominator: number;
  denominatorLabel: string;
  formula: string;
}

export interface FlaggedItem {
  activityId: string;
  activityName: string;
  reason: string;
}

export interface ProjectSummary {
  totalActivities: number;
  taskActivities: number;
  milestones: number;
  startMilestones: number;
  finishMilestones: number;
  loeActivities: number;
  totalRelationships: number;
  fsRelationships: number;
  ssRelationships: number;
  ffRelationships: number;
  sfRelationships: number;
  completedActivities: number;
  inProgressActivities: number;
  notStartedActivities: number;
  // Derived counts used as denominators in scoring
  nonLOEActivities: number;
  incompleteNonLOE: number;
  incompleteTaskActivities: number;
  relationshipsWithLag: number;
  activitiesWithConstraints: number;
}

export interface DCMAScoreResult {
  projectName: string;
  dataDate: string;
  plannedStart: string;
  scheduledFinish: string;
  totalScore: number;
  grade: string;
  gradeColor: string;
  criterionResults: CriterionResult[];
  summary: ProjectSummary;
}

// Helper to compute total float in days for a single activity
function computeTotalFloatDays(activity: P6Activity, hoursPerDay: number): number | null {
  if (!activity.earlyFinishDate || !activity.lateFinishDate) return null;

  const earlyFinish = new Date(activity.earlyFinishDate).getTime();
  const lateFinish = new Date(activity.lateFinishDate).getTime();

  if (isNaN(earlyFinish) || isNaN(lateFinish)) return null;

  // Float in hours = lateFinish - earlyFinish
  const floatMs = lateFinish - earlyFinish;
  const floatHours = floatMs / (1000 * 60 * 60);

  // Convert to days using calendar hours per day
  return floatHours / hoursPerDay;
}

function getHoursPerDay(activity: P6Activity, project: P6Project): number {
  const cal = project.calendars.get(activity.calendarObjectId);
  return cal?.hoursPerDay || 8;
}

// Check if activity is a "task" (non-milestone, non-LOE)
function isTask(a: P6Activity): boolean {
  return a.type === 'Task Dependent';
}

function isLOE(a: P6Activity): boolean {
  return a.type === 'Level of Effort';
}

function isMilestone(a: P6Activity): boolean {
  return a.type === 'Start Milestone' || a.type === 'Finish Milestone';
}

function isIncompleteNonLOE(a: P6Activity): boolean {
  return a.status !== 'Completed' && !isLOE(a);
}

// Evaluate which tier a value falls into for a tiered criterion
function evaluateTiered(
  value: number,
  criterion: ScoringCriterion
): { tier: 'good' | 'acceptable' | 'bad'; scoreImpact: number; thresholdDescription: string } {
  const tiers = criterion.tiers!;

  // Check good first
  if (tiers.good.thresholdType === 'gt' && value > (tiers.good.minValue ?? 0)) {
    return {
      tier: 'good',
      scoreImpact: tiers.good.scoreImpact,
      thresholdDescription: tiers.good.threshold,
    };
  }
  if (tiers.good.thresholdType === 'lt' && value < (tiers.good.maxValue ?? 100)) {
    return {
      tier: 'good',
      scoreImpact: tiers.good.scoreImpact,
      thresholdDescription: tiers.good.threshold,
    };
  }
  if (
    tiers.good.thresholdType === 'range' &&
    value >= (tiers.good.minValue ?? 0) &&
    value <= (tiers.good.maxValue ?? 100)
  ) {
    return {
      tier: 'good',
      scoreImpact: tiers.good.scoreImpact,
      thresholdDescription: tiers.good.threshold,
    };
  }

  // Check acceptable
  if (
    tiers.acceptable.thresholdType === 'range' &&
    value >= (tiers.acceptable.minValue ?? 0) &&
    value <= (tiers.acceptable.maxValue ?? 100)
  ) {
    return {
      tier: 'acceptable',
      scoreImpact: tiers.acceptable.scoreImpact,
      thresholdDescription: tiers.acceptable.threshold,
    };
  }
  if (tiers.acceptable.thresholdType === 'gt' && value > (tiers.acceptable.minValue ?? 0)) {
    return {
      tier: 'acceptable',
      scoreImpact: tiers.acceptable.scoreImpact,
      thresholdDescription: tiers.acceptable.threshold,
    };
  }
  if (tiers.acceptable.thresholdType === 'lt' && value < (tiers.acceptable.maxValue ?? 100)) {
    return {
      tier: 'acceptable',
      scoreImpact: tiers.acceptable.scoreImpact,
      thresholdDescription: tiers.acceptable.threshold,
    };
  }

  // Default to bad
  return {
    tier: 'bad',
    scoreImpact: tiers.bad.scoreImpact,
    thresholdDescription: tiers.bad.threshold,
  };
}

function evaluateMiddleBest(
  value: number,
  criterion: ScoringCriterion
): {
  tier: 'good' | 'acceptable' | 'badLow' | 'badHigh';
  scoreImpact: number;
  thresholdDescription: string;
} {
  const tiers = criterion.middleBestTiers!;

  // Check good (middle range)
  if (value >= (tiers.good.minValue ?? 0) && value <= (tiers.good.maxValue ?? 100)) {
    return {
      tier: 'good',
      scoreImpact: tiers.good.scoreImpact,
      thresholdDescription: tiers.good.threshold,
    };
  }

  // Check acceptable low (between badLow and good)
  if (value >= (tiers.acceptable.minValue ?? 0) && value < (tiers.acceptable.maxValue ?? 100)) {
    return {
      tier: 'acceptable',
      scoreImpact: tiers.acceptable.scoreImpact,
      thresholdDescription: tiers.acceptable.threshold,
    };
  }

  // Check acceptable high (between good and badHigh) - optional zone
  if (
    tiers.acceptableHigh &&
    value > (tiers.good.maxValue ?? 100) &&
    value <= (tiers.acceptableHigh.maxValue ?? 100)
  ) {
    return {
      tier: 'acceptable',
      scoreImpact: tiers.acceptableHigh.scoreImpact,
      thresholdDescription: tiers.acceptableHigh.threshold,
    };
  }

  // Check bad low
  if (value < (tiers.badLow.maxValue ?? 0)) {
    return {
      tier: 'badLow',
      scoreImpact: tiers.badLow.scoreImpact,
      thresholdDescription: tiers.badLow.threshold,
    };
  }

  // Bad high
  return {
    tier: 'badHigh',
    scoreImpact: tiers.badHigh.scoreImpact,
    thresholdDescription: tiers.badHigh.threshold,
  };
}

interface ComputeContext {
  project: P6Project;
  allActivities: P6Activity[];
  taskActivities: P6Activity[];
  incompleteNonLOE: P6Activity[];
  nonLOEActivities: P6Activity[];
  incompleteTaskActivities: P6Activity[];
  relationships: P6Project['relationships'];
  fsRels: P6Project['relationships'];
  ssRels: P6Project['relationships'];
  ffRels: P6Project['relationships'];
  hasPredecessor: Set<number>;
  hasSuccessor: Set<number>;
}

function computeRelationshipRatio(
  criterion: ScoringCriterion,
  ctx: ComputeContext,
  base: Partial<CriterionResult>
): CriterionResult {
  // Relationship Ratio = total relationships / total non-LOE activities
  const denom = ctx.nonLOEActivities.length;
  const num = ctx.relationships.length;
  const ratio = denom > 0 ? num / denom : 0;
  const { tier, scoreImpact, thresholdDescription } = evaluateTiered(ratio, criterion);

  return {
    ...base,
    computedValue: ratio,
    displayValue: ratio.toFixed(2),
    tier,
    tierLabel: tier === 'good' ? 'Good' : tier === 'acceptable' ? 'Acceptable' : 'Bad',
    scoreImpact: criterion.enabled ? scoreImpact : 0,
    thresholdDescription,
    flaggedItems: [],
    numerator: num,
    denominator: denom,
    denominatorLabel: 'non-LOE activities',
    formula: `${num} relationships / ${denom} non-LOE activities = ${ratio.toFixed(2)}`,
  } as CriterionResult;
}

function computeRelTypePercent(
  criterion: ScoringCriterion,
  ctx: ComputeContext,
  base: Partial<CriterionResult>,
  typeName: string,
  matchingRels: P6Project['relationships']
): CriterionResult {
  const total = ctx.relationships.length;
  const num = matchingRels.length;
  const pct = total > 0 ? (num / total) * 100 : 0;
  const { tier, scoreImpact, thresholdDescription } = evaluateTiered(pct, criterion);

  return {
    ...base,
    computedValue: pct,
    displayValue: `${pct.toFixed(1)}%`,
    tier,
    tierLabel: tier === 'good' ? 'Good' : tier === 'acceptable' ? 'Acceptable' : 'Bad',
    scoreImpact: criterion.enabled ? scoreImpact : 0,
    thresholdDescription,
    flaggedItems: [],
    numerator: num,
    denominator: total,
    denominatorLabel: 'total relationships',
    formula: `${num} ${typeName} / ${total} total relationships = ${pct.toFixed(1)}%`,
  } as CriterionResult;
}

function computeMissingDuration(
  criterion: ScoringCriterion,
  ctx: ComputeContext,
  base: Partial<CriterionResult>
): CriterionResult {
  // % of task-dependent (non-milestone, non-LOE) activities with zero or missing planned duration
  // Exclude completed activities from this check
  const activities = ctx.incompleteTaskActivities;
  const flagged: FlaggedItem[] = [];

  for (const a of activities) {
    if (a.plannedDuration === 0 && a.remainingDuration === 0 && a.atCompletionDuration === 0) {
      flagged.push({ activityId: a.id, activityName: a.name, reason: 'Duration is 0' });
    }
  }

  const denom = activities.length;
  const num = flagged.length;
  const pct = denom > 0 ? (num / denom) * 100 : 0;
  const multiplier = criterion.percentageMultiplier ?? 1;
  const deduction = -(pct * multiplier);

  return {
    ...base,
    computedValue: pct,
    displayValue: `${pct.toFixed(1)}%`,
    tier: pct === 0 ? 'good' : pct <= 5 ? 'acceptable' : 'bad',
    tierLabel: pct === 0 ? 'Good' : pct <= 5 ? 'Acceptable' : 'Bad',
    scoreImpact: criterion.enabled ? deduction : 0,
    thresholdDescription: `${pct.toFixed(1)}% x ${multiplier} = ${Math.abs(deduction).toFixed(1)} pts deducted`,
    flaggedItems: flagged,
    numerator: num,
    denominator: denom,
    denominatorLabel: 'incomplete task activities',
    formula: `${num} missing duration / ${denom} incomplete tasks = ${pct.toFixed(1)}%`,
  } as CriterionResult;
}

function computeMissingLogic(
  criterion: ScoringCriterion,
  ctx: ComputeContext,
  base: Partial<CriterionResult>
): CriterionResult {
  // % of non-LOE activities missing predecessor OR successor
  const activities = ctx.nonLOEActivities;
  const flagged: FlaggedItem[] = [];

  for (const a of activities) {
    const hasPred = ctx.hasPredecessor.has(a.objectId);
    const hasSucc = ctx.hasSuccessor.has(a.objectId);

    // Start Milestones are permitted to have no predecessor (they only need a successor)
    const isStartMilestone = a.type === 'Start Milestone';
    // Finish Milestones are permitted to have no successor (they only need a predecessor)
    const isFinishMilestone = a.type === 'Finish Milestone';

    const needsPred = !isStartMilestone;
    const needsSucc = !isFinishMilestone;

    const missingPred = needsPred && !hasPred;
    const missingSucc = needsSucc && !hasSucc;

    if (missingPred && missingSucc) {
      flagged.push({
        activityId: a.id,
        activityName: a.name,
        reason: 'No predecessor and no successor',
      });
    } else if (missingPred) {
      flagged.push({ activityId: a.id, activityName: a.name, reason: 'No predecessor' });
    } else if (missingSucc) {
      flagged.push({ activityId: a.id, activityName: a.name, reason: 'No successor' });
    } else if (isStartMilestone && !hasSucc) {
      // Start milestone must have at least a successor
      flagged.push({
        activityId: a.id,
        activityName: a.name,
        reason: 'Start Milestone has no successor',
      });
    } else if (isFinishMilestone && !hasPred) {
      // Finish milestone must have at least a predecessor
      flagged.push({
        activityId: a.id,
        activityName: a.name,
        reason: 'Finish Milestone has no predecessor',
      });
    }
  }

  const denom = activities.length;
  const num = flagged.length;
  const pct = denom > 0 ? (num / denom) * 100 : 0;
  const multiplier = criterion.percentageMultiplier ?? 1;
  const deduction = -(pct * multiplier);

  return {
    ...base,
    computedValue: pct,
    displayValue: `${pct.toFixed(1)}%`,
    tier: pct === 0 ? 'good' : pct <= 5 ? 'acceptable' : 'bad',
    tierLabel: pct === 0 ? 'Good' : pct <= 5 ? 'Acceptable' : 'Bad',
    scoreImpact: criterion.enabled ? deduction : 0,
    thresholdDescription: `${pct.toFixed(1)}% x ${multiplier} = ${Math.abs(deduction).toFixed(1)} pts deducted`,
    flaggedItems: flagged,
    numerator: num,
    denominator: denom,
    denominatorLabel: 'non-LOE activities',
    formula: `${num} missing logic / ${denom} non-LOE activities = ${pct.toFixed(1)}%`,
  } as CriterionResult;
}

function computeNumberOfLags(
  criterion: ScoringCriterion,
  ctx: ComputeContext,
  base: Partial<CriterionResult>
): CriterionResult {
  const total = ctx.relationships.length;
  const lagged = ctx.relationships.filter((r) => r.lag !== 0);
  const num = lagged.length;
  const pct = total > 0 ? (num / total) * 100 : 0;
  const { tier, scoreImpact, thresholdDescription } = evaluateTiered(pct, criterion);

  const flagged: FlaggedItem[] = lagged.map((r) => {
    const predAct = ctx.allActivities.find((a) => a.objectId === r.predecessorActivityObjectId);
    const succAct = ctx.allActivities.find((a) => a.objectId === r.successorActivityObjectId);
    const hoursPerDay = predAct ? getHoursPerDay(predAct, ctx.project) : 8;
    const lagDays = r.lag / hoursPerDay;
    return {
      activityId: `${predAct?.id || '?'} -> ${succAct?.id || '?'}`,
      activityName: `${predAct?.name || '?'} -> ${succAct?.name || '?'}`,
      reason: `Lag: ${lagDays >= 0 ? '+' : ''}${lagDays.toFixed(1)} days (${r.type})`,
    };
  });

  return {
    ...base,
    computedValue: pct,
    displayValue: `${pct.toFixed(1)}%`,
    tier,
    tierLabel: tier === 'good' ? 'Good' : tier === 'acceptable' ? 'Acceptable' : 'Bad',
    scoreImpact: criterion.enabled ? scoreImpact : 0,
    thresholdDescription,
    flaggedItems: flagged,
    numerator: num,
    denominator: total,
    denominatorLabel: 'total relationships',
    formula: `${num} lagged / ${total} total relationships = ${pct.toFixed(1)}%`,
  } as CriterionResult;
}

// Only these constraint types are considered "hard" and should result in deductions.
// Soft constraints (e.g., "As Late As Possible", "Start On or After") are acceptable.
const HARD_CONSTRAINT_TYPES = new Set([
  'Must Finish On', // MFO
  'Must Start On', // MSO
  'Start No Later Than', // SNLT
  'Finish No Later Than', // FNLT
]);

function computeNumberOfConstraints(
  criterion: ScoringCriterion,
  ctx: ComputeContext,
  base: Partial<CriterionResult>
): CriterionResult {
  const activities = ctx.nonLOEActivities;
  const flagged: FlaggedItem[] = [];

  for (const a of activities) {
    if (a.primaryConstraintType && HARD_CONSTRAINT_TYPES.has(a.primaryConstraintType)) {
      flagged.push({
        activityId: a.id,
        activityName: a.name,
        reason: `Hard Constraint: ${a.primaryConstraintType}${a.primaryConstraintDate ? ` (${new Date(a.primaryConstraintDate).toLocaleDateString()})` : ''}`,
      });
    }
  }

  const denom = activities.length;
  const num = flagged.length;
  const pct = denom > 0 ? (num / denom) * 100 : 0;
  const multiplier = criterion.percentageMultiplier ?? 1;
  const deduction = -(pct * multiplier);

  return {
    ...base,
    computedValue: pct,
    displayValue: `${pct.toFixed(1)}%`,
    tier: pct === 0 ? 'good' : pct <= 5 ? 'acceptable' : 'bad',
    tierLabel: pct === 0 ? 'Good' : pct <= 5 ? 'Acceptable' : 'Bad',
    scoreImpact: criterion.enabled ? deduction : 0,
    thresholdDescription: `${pct.toFixed(1)}% x ${multiplier} = ${Math.abs(deduction).toFixed(1)} pts deducted`,
    flaggedItems: flagged,
    numerator: num,
    denominator: denom,
    denominatorLabel: 'non-LOE activities',
    formula: `${num} hard constrained / ${denom} non-LOE activities = ${pct.toFixed(1)}%`,
  } as CriterionResult;
}

function computeHighFloat(
  criterion: ScoringCriterion,
  ctx: ComputeContext,
  base: Partial<CriterionResult>
): CriterionResult {
  const daysThreshold = criterion.daysThreshold ?? 44;
  const activities = ctx.incompleteNonLOE;
  const flagged: FlaggedItem[] = [];

  for (const a of activities) {
    const hoursPerDay = getHoursPerDay(a, ctx.project);
    const floatDays = computeTotalFloatDays(a, hoursPerDay);
    if (floatDays !== null && floatDays > daysThreshold) {
      flagged.push({
        activityId: a.id,
        activityName: a.name,
        reason: `Total Float: ${floatDays.toFixed(1)} days (threshold: ${daysThreshold} days)`,
      });
    }
  }

  const denom = activities.length;
  const num = flagged.length;
  const pct = denom > 0 ? (num / denom) * 100 : 0;
  const { tier, scoreImpact, thresholdDescription } = evaluateTiered(pct, criterion);

  return {
    ...base,
    computedValue: pct,
    displayValue: `${pct.toFixed(1)}%`,
    tier,
    tierLabel: tier === 'good' ? 'Good' : tier === 'acceptable' ? 'Acceptable' : 'Bad',
    scoreImpact: criterion.enabled ? scoreImpact : 0,
    thresholdDescription,
    flaggedItems: flagged,
    numerator: num,
    denominator: denom,
    denominatorLabel: 'incomplete non-LOE activities',
    formula: `${num} with float > ${daysThreshold}d / ${denom} incomplete non-LOE = ${pct.toFixed(1)}%`,
  } as CriterionResult;
}

function computeHighDuration(
  criterion: ScoringCriterion,
  ctx: ComputeContext,
  base: Partial<CriterionResult>
): CriterionResult {
  const daysThreshold = criterion.daysThreshold ?? 44;
  const activities = ctx.incompleteTaskActivities;
  const flagged: FlaggedItem[] = [];

  for (const a of activities) {
    const hoursPerDay = getHoursPerDay(a, ctx.project);
    const durationDays = a.plannedDuration / hoursPerDay;
    if (durationDays > daysThreshold) {
      flagged.push({
        activityId: a.id,
        activityName: a.name,
        reason: `Duration: ${durationDays.toFixed(1)} days (threshold: ${daysThreshold} days)`,
      });
    }
  }

  const denom = activities.length;
  const num = flagged.length;
  const pct = denom > 0 ? (num / denom) * 100 : 0;
  const { tier, scoreImpact, thresholdDescription } = evaluateTiered(pct, criterion);

  return {
    ...base,
    computedValue: pct,
    displayValue: `${pct.toFixed(1)}%`,
    tier,
    tierLabel: tier === 'good' ? 'Good' : tier === 'acceptable' ? 'Acceptable' : 'Bad',
    scoreImpact: criterion.enabled ? scoreImpact : 0,
    thresholdDescription,
    flaggedItems: flagged,
    numerator: num,
    denominator: denom,
    denominatorLabel: 'incomplete task activities',
    formula: `${num} with duration > ${daysThreshold}d / ${denom} incomplete tasks = ${pct.toFixed(1)}%`,
  } as CriterionResult;
}

function computeCriticalPathPercent(ctx: ComputeContext): {
  pct: number;
  flagged: FlaggedItem[];
  num: number;
  denom: number;
} {
  // Critical activities = incomplete non-LOE activities with total float <= 0
  const activities = ctx.incompleteNonLOE;
  const flagged: FlaggedItem[] = [];

  for (const a of activities) {
    const hoursPerDay = getHoursPerDay(a, ctx.project);
    const floatDays = computeTotalFloatDays(a, hoursPerDay);
    if (floatDays !== null && floatDays <= 0) {
      flagged.push({
        activityId: a.id,
        activityName: a.name,
        reason: `Total Float: ${floatDays.toFixed(1)} days (critical)`,
      });
    }
  }

  const denom = activities.length;
  const num = flagged.length;
  const pct = denom > 0 ? (num / denom) * 100 : 0;
  return { pct, flagged, num, denom };
}

function computeCriticalPath(
  criterion: ScoringCriterion,
  ctx: ComputeContext,
  base: Partial<CriterionResult>
): CriterionResult {
  const { pct, flagged, num, denom } = computeCriticalPathPercent(ctx);
  const { tier, scoreImpact, thresholdDescription } = evaluateMiddleBest(pct, criterion);

  return {
    ...base,
    computedValue: pct,
    displayValue: `${pct.toFixed(1)}%`,
    tier,
    tierLabel:
      tier === 'good'
        ? 'Good'
        : tier === 'acceptable'
          ? 'Acceptable'
          : tier === 'badLow'
            ? 'Too Low'
            : 'Too High',
    scoreImpact: criterion.enabled ? scoreImpact : 0,
    thresholdDescription,
    flaggedItems: flagged,
    numerator: num,
    denominator: denom,
    denominatorLabel: 'incomplete non-LOE activities',
    formula: `${num} critical (float <= 0) / ${denom} incomplete non-LOE = ${pct.toFixed(1)}%`,
  } as CriterionResult;
}

function computeCriterion(criterion: ScoringCriterion, ctx: ComputeContext): CriterionResult {
  const base: Partial<CriterionResult> = {
    criterionId: criterion.id,
    criterionName: criterion.name,
    category: criterion.category,
    categoryLabel: categoryLabels[criterion.category] || criterion.category,
    enabled: criterion.enabled,
  };

  switch (criterion.id) {
    case 'relationship-ratio':
      return computeRelationshipRatio(criterion, ctx, base);
    case 'finish-to-start':
      return computeRelTypePercent(criterion, ctx, base, 'Finish to Start', ctx.fsRels);
    case 'start-to-start':
      return computeRelTypePercent(criterion, ctx, base, 'Start to Start', ctx.ssRels);
    case 'finish-to-finish':
      return computeRelTypePercent(criterion, ctx, base, 'Finish to Finish', ctx.ffRels);
    case 'missing-duration':
      return computeMissingDuration(criterion, ctx, base);
    case 'missing-logic':
      return computeMissingLogic(criterion, ctx, base);
    case 'number-of-lags':
      return computeNumberOfLags(criterion, ctx, base);
    case 'number-of-constraints':
      return computeNumberOfConstraints(criterion, ctx, base);
    case 'high-float':
      return computeHighFloat(criterion, ctx, base);
    case 'high-duration':
      return computeHighDuration(criterion, ctx, base);
    case 'critical-path-percent':
      return computeCriticalPath(criterion, ctx, base);
    default:
      return {
        ...base,
        computedValue: 0,
        displayValue: 'N/A',
        tier: 'n/a',
        tierLabel: 'N/A',
        scoreImpact: 0,
        thresholdDescription: 'Unknown criterion',
        flaggedItems: [],
        numerator: 0,
        denominator: 0,
        denominatorLabel: 'N/A',
        formula: 'N/A',
      } as CriterionResult;
  }
}

export function scoreProject(
  project: P6Project,
  criteria: ScoringCriterion[] = defaultCriteria,
  gradeThresholds: GradeThreshold[] = defaultGradeThresholds
): DCMAScoreResult {
  const { activities, relationships } = project;

  // Categorize activities
  const allActivities = activities;
  const taskActivities = allActivities.filter(isTask);
  const loeActivities = allActivities.filter(isLOE);
  const milestoneActivities = allActivities.filter(isMilestone);
  const startMilestones = allActivities.filter((a) => a.type === 'Start Milestone');
  const finishMilestones = allActivities.filter((a) => a.type === 'Finish Milestone');
  const completedActivities = allActivities.filter((a) => a.status === 'Completed');
  const inProgressActivities = allActivities.filter((a) => a.status === 'In Progress');
  const notStartedActivities = allActivities.filter((a) => a.status === 'Not Started');

  // Non-LOE, non-completed activities (for float and critical path analysis)
  const incompleteNonLOE = allActivities.filter(isIncompleteNonLOE);

  // Non-LOE activities (for missing logic check)
  const nonLOEActivities = allActivities.filter((a) => !isLOE(a));

  // Non-milestone, non-LOE task activities (for duration checks)
  const taskOnlyActivities = taskActivities;

  // Incomplete task activities (non-milestone, non-LOE, non-completed)
  const incompleteTaskActivities = taskOnlyActivities.filter((a) => a.status !== 'Completed');

  // Categorize relationships
  const fsRels = relationships.filter((r) => r.type === 'Finish to Start');
  const ssRels = relationships.filter((r) => r.type === 'Start to Start');
  const ffRels = relationships.filter((r) => r.type === 'Finish to Finish');
  const sfRels = relationships.filter((r) => r.type === 'Start to Finish');

  // Build predecessor/successor sets for missing logic check
  const hasPredecessor = new Set<number>();
  const hasSuccessor = new Set<number>();
  for (const rel of relationships) {
    hasPredecessor.add(rel.successorActivityObjectId);
    hasSuccessor.add(rel.predecessorActivityObjectId);
  }

  // Precompute lagged relationships and hard-constrained activities for summary
  const laggedRelationships = relationships.filter((r) => r.lag !== 0);
  const constrainedActivities = nonLOEActivities.filter(
    (a) => a.primaryConstraintType && HARD_CONSTRAINT_TYPES.has(a.primaryConstraintType)
  );

  // Summary
  const summary: ProjectSummary = {
    totalActivities: allActivities.length,
    taskActivities: taskActivities.length,
    milestones: milestoneActivities.length,
    startMilestones: startMilestones.length,
    finishMilestones: finishMilestones.length,
    loeActivities: loeActivities.length,
    totalRelationships: relationships.length,
    fsRelationships: fsRels.length,
    ssRelationships: ssRels.length,
    ffRelationships: ffRels.length,
    sfRelationships: sfRels.length,
    completedActivities: completedActivities.length,
    inProgressActivities: inProgressActivities.length,
    notStartedActivities: notStartedActivities.length,
    nonLOEActivities: nonLOEActivities.length,
    incompleteNonLOE: incompleteNonLOE.length,
    incompleteTaskActivities: incompleteTaskActivities.length,
    relationshipsWithLag: laggedRelationships.length,
    activitiesWithConstraints: constrainedActivities.length,
  };

  // Compute each criterion
  const criterionResults: CriterionResult[] = [];
  let totalScore = 100;

  for (const criterion of criteria) {
    const result = computeCriterion(criterion, {
      project,
      allActivities,
      taskActivities: taskOnlyActivities,
      incompleteNonLOE,
      nonLOEActivities,
      incompleteTaskActivities,
      relationships,
      fsRels,
      ssRels,
      ffRels,
      hasPredecessor,
      hasSuccessor,
    });

    criterionResults.push(result);

    if (criterion.enabled) {
      totalScore += result.scoreImpact; // scoreImpact is negative or zero
    }
  }

  // Clamp score
  totalScore = Math.max(0, Math.min(100, totalScore));

  // Determine grade
  const gradeInfo =
    gradeThresholds.find((g) => totalScore >= g.minScore && totalScore <= g.maxScore) ||
    gradeThresholds[gradeThresholds.length - 1];

  return {
    projectName: project.name,
    dataDate: project.dataDate,
    plannedStart: project.plannedStartDate,
    scheduledFinish: project.scheduledFinishDate,
    totalScore: Math.round(totalScore * 10) / 10,
    grade: gradeInfo.grade,
    gradeColor: gradeInfo.color,
    criterionResults,
    summary,
  };
}
